import {
  DbotEventDispatcher,
  DecisionContext,
  ProjectCompany,
  ProjectStatus,
} from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import { DecisionType } from "@/common/decisions";
import { CompanyRole } from "@/types/CompanyInfo";
import { getCompanyCEO } from "@/utils/other";
import {
  boardMeetingParticipantsPanel,
  isRoleAttendingBoardMeeting,
} from "@/dbot/boardMeetingMinutes/boardMeetingMinutesDomain";

export type NoManagingDirector = {
  type: DecisionType.noManagingDirector;
};

export class NoManagingDirectorForm {
  readonly type = DecisionType.noManagingDirector;
  readonly context: DecisionContext<NoManagingDirector>;
  readonly data?: NoManagingDirector;
  readonly dispatch?: DbotEventDispatcher;

  constructor(
    context: DecisionContext<NoManagingDirector>,
    data?: NoManagingDirector,
    dispatch?: DbotEventDispatcher,
  ) {
    makeAutoObservable(this, { type: false, data: false, dispatch: false }, { autoBind: true });
    this.context = context;
    this.data = data;
    this.dispatch = dispatch;
  }

  private managingDirectorFor(orgNo: string): CompanyRole | undefined {
    const roles = this.context.contextData[orgNo]?.initialState?.roles;
    return roles && getCompanyCEO(roles);
  }

  get managingDirector(): CompanyRole | undefined {
    return this.managingDirectorFor(this.context.orgNo);
  }

  get companiesWithoutManagingDirector(): ProjectCompany[] {
    return this.context.companies.filter((c) => this.managingDirectorFor(c.orgNo) === undefined);
  }

  get allCompaniesHaveManagingDirector(): boolean {
    return (
      this.context.projectStatus !== ProjectStatus.DRAFT ||
      this.companiesWithoutManagingDirector.length === 0
    );
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isManagingDirectorAttendingBoardMeeting(): boolean {
    return isRoleAttendingBoardMeeting(
      this.context.currentDocuments,
      this.managingDirector?.companyroleId,
    );
  }

  openBoardMeetingParticipants() {
    const panel = boardMeetingParticipantsPanel(this.context.currentDocuments);
    if (panel !== undefined) {
      this.dispatch?.({ type: "dbot/open-details-panel", panel });
    }
  }
}
